/**
 * Autenticação do painel.
 *
 * No modo demo não há Firebase Auth, então um usuário fictício é injetado — o resto do painel não
 * precisa saber em qual modo está rodando. Em produção é `signInWithPopup` com o provedor Google,
 * que precisa estar habilitado no console (Authentication → Sign-in method); sem isso o popup
 * devolve `auth/operation-not-allowed`, e a mensagem abaixo diz exatamente isso em vez de deixar o
 * usuário adivinhando.
 */

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
  signOut,
  type User,
} from 'firebase/auth';
import { getAuthClient } from '../lib/firebase.js';
import { isDemoMode } from '../data/index.js';

export interface AuthUser {
  uid: string;
  displayName: string;
  email: string;
  photoURL: string | null;
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
  signIn(): Promise<void>;
  signOutUser(): Promise<void>;
}

const DEMO_USER: AuthUser = {
  uid: 'demo-owner',
  displayName: 'Modo demo',
  email: 'demo@local',
  photoURL: null,
};

const AuthContext = createContext<AuthContextValue | null>(null);

function toAuthUser(user: User): AuthUser {
  return {
    uid: user.uid,
    displayName: user.displayName ?? user.email ?? 'Sem nome',
    email: user.email ?? '',
    photoURL: user.photoURL ?? null,
  };
}

function describeAuthError(error: unknown): string {
  const code = (error as { code?: string })?.code ?? '';
  if (code === 'auth/operation-not-allowed') {
    return 'O provedor Google não está habilitado neste projeto. Ative em Authentication → Sign-in method no console do Firebase.';
  }
  if (code === 'auth/unauthorized-domain') {
    return 'Este domínio não está autorizado no Firebase Auth. Adicione-o em Authentication → Settings → Authorized domains.';
  }
  if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
    return 'A janela de login foi fechada antes de concluir.';
  }
  return error instanceof Error ? error.message : String(error);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(isDemoMode ? DEMO_USER : null);
  const [loading, setLoading] = useState(!isDemoMode);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isDemoMode) return;
    const unsubscribe = onAuthStateChanged(getAuthClient(), (firebaseUser) => {
      setUser(firebaseUser ? toAuthUser(firebaseUser) : null);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      error,
      async signIn() {
        if (isDemoMode) {
          setUser(DEMO_USER);
          return;
        }
        setError(null);
        try {
          const provider = new GoogleAuthProvider();
          await signInWithPopup(getAuthClient(), provider);
        } catch (caught) {
          setError(describeAuthError(caught));
        }
      },
      async signOutUser() {
        if (isDemoMode) {
          setUser(null);
          return;
        }
        await signOut(getAuthClient());
      },
    }),
    [user, loading, error],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth precisa estar dentro de <AuthProvider>.');
  return context;
}
